import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import type { FilenameMatch } from '../../utils/documentSolutionMatch';

interface FilenameMatchPickerProps {
  matches: FilenameMatch[];
  /** Called with the client project the user picked for the open document. */
  onPick: (match: FilenameMatch) => void;
  /** "Choose a project manually" — abandon the suggestions. */
  onChooseManually: () => void;
  dir?: string;
}

/**
 * "Which project does this document belong to?" — the two-step (project →
 * client project) picker shown when the open document's filename matches more
 * than one client project.
 */
export const FilenameMatchPicker: React.FC<FilenameMatchPickerProps> = ({
  matches,
  onPick,
  onChooseManually,
  dir,
}) => {
  const { t } = useTranslation('taskpane');
  const [projectId, setProjectId] = React.useState<string | null>(null);

  const projects = React.useMemo(() => {
    const seen = new Map<string, string>();
    matches.forEach((m) => {
      if (!seen.has(m.project_id)) seen.set(m.project_id, m.project_name);
    });
    return Array.from(seen.entries()).map(([project_id, project_name]) => ({ project_id, project_name }));
  }, [matches]);

  const subProjects = React.useMemo(
    () => (projectId == null ? [] : matches.filter((m) => m.project_id === projectId)),
    [matches, projectId],
  );

  // A single parent project has nothing to disambiguate — go straight to its client projects.
  const effectiveProjectId = projects.length === 1 ? projects[0].project_id : projectId;
  const effectiveSubProjects =
    projects.length === 1 ? matches.filter((m) => m.project_id === projects[0].project_id) : subProjects;

  return (
    <div className="clauses-followup" dir={dir}>
      {effectiveProjectId == null ? (
        <>
          <h3 className="clauses-question-text">{t('solutions.projectPick.whatProject')}</h3>
          <div className="clauses-options-list">
            {projects.map((p) => (
              <button
                key={p.project_id}
                type="button"
                className="clauses-option-item"
                onClick={() => setProjectId(p.project_id)}
              >
                <span className="clauses-option-project" dir="auto">
                  {p.project_name}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          {projects.length > 1 && (
            <button type="button" className="clauses-back-link" onClick={() => setProjectId(null)}>
              <ChevronLeft size={14} className="clauses-back-icon" aria-hidden />
              {t('actions.back', { ns: 'common' })}
            </button>
          )}
          <h3 className="clauses-question-text">{t('solutions.projectPick.whatSubProject')}</h3>
          <div className="clauses-options-list">
            {effectiveSubProjects.map((m) => (
              <button
                key={m.sub_project_id}
                type="button"
                className="clauses-option-item"
                onClick={() => onPick(m)}
              >
                <span className="clauses-option-sub" dir="auto">
                  {m.sub_project_name}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
      <button type="button" className="clauses-manual-toggle" onClick={onChooseManually}>
        {t('solutions.projectPick.chooseManually')}
      </button>
    </div>
  );
};

export default FilenameMatchPicker;
